/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Unified Media Gallery & Asset Manager View (Task 10.4.6)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  Search,
  Heart,
  Trash2,
  CheckSquare,
  Sparkles,
  Layers,
  Database,
  Image as ImageIcon,
  Film,
  Volume2,
  X,
  Check,
} from 'lucide-react';
import { useTranslation } from '../i18n';
import {
  useMediaGalleryStore,
  MediaAsset,
  MediaAssetType,
  MediaSortOption,
} from '../stores/useMediaGalleryStore';
import { MediaGridCard } from '../components/gallery/MediaGridCard';
import { AssetDetailDrawer } from '../components/gallery/AssetDetailDrawer';

interface MediaGalleryViewProps {
  onNavigateToImage?: (prompt?: string) => void;
  onNavigateToVideo?: (prompt?: string) => void;
  onNavigateToAvatar?: (prompt?: string) => void;
}

export const MediaGalleryView: React.FC<MediaGalleryViewProps> = ({
  onNavigateToImage,
  onNavigateToVideo,
  onNavigateToAvatar,
}) => {
  const { t } = useTranslation();

  const {
    searchQuery,
    typeFilter,
    modelFilter,
    sortOption,
    onlyFavorites,
    activeAsset,
    isSelectionMode,
    selectedIds,
    setSearchQuery,
    setTypeFilter,
    setModelFilter,
    setSortOption,
    setOnlyFavorites,
    setActiveAsset,
    toggleSelectionMode,
    toggleSelect,
    selectAll,
    deselectAll,
    toggleFavorite,
    renameAsset,
    deleteAsset,
    batchDelete,
    getFilteredAssets,
    getStorageMetrics,
  } = useMediaGalleryStore();

  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [showBatchDeleteModal, setShowBatchDeleteModal] = useState<boolean>(false);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const filteredAssets = getFilteredAssets();
  const metrics = getStorageMetrics();

  const handleBatchDelete = () => {
    const count = selectedIds.length;
    batchDelete(selectedIds);
    setShowBatchDeleteModal(false);
    showToast(t('gallery.batchDeletedSuccess', { count }));
  };

  const handleSingleDelete = (id: string) => {
    deleteAsset(id);
    showToast(t('gallery.assetDeletedSuccess'));
  };

  const handleRename = (id: string, newTitle: string) => {
    renameAsset(id, newTitle);
    showToast(t('gallery.assetRenamedSuccess'));
  };

  const handleReusePrompt = (asset: MediaAsset) => {
    if (!asset.prompt) return;

    if (asset.assetType === 'image') {
      onNavigateToImage?.(asset.prompt);
    } else if (asset.generatorCode.includes('synthesia') || asset.generatorCode.includes('avatar')) {
      onNavigateToAvatar?.(asset.prompt);
    } else {
      onNavigateToVideo?.(asset.prompt);
    }
  };

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-xs font-semibold shadow-xl border border-emerald-500/30 animate-in fade-in slide-in-from-bottom-2 duration-200">
          <Check className="w-4 h-4" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* 1. Header & Overview Metrics */}
      <div className="relative overflow-hidden rounded-2xl border border-surface-border bg-surface p-6 sm:p-8 shadow-glass">
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-accent/10 blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wider font-semibold text-primary">
                {t('gallery.title')}
              </span>
              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-bold bg-primary/15 text-primary border border-primary/20">
                Cloudflare R2 CDN
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold text-foreground tracking-tight">
              {t('gallery.title')}
            </h1>

            <p className="text-foreground-muted text-sm max-w-2xl">
              {t('gallery.subtitle')}
            </p>
          </div>

          {/* Storage & Usage Pills */}
          <div className="flex flex-wrap items-center gap-2.5">
            <div className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-surface-border bg-surface-elevated text-xs font-mono">
              <Database className="w-4 h-4 text-primary" />
              <span>{t('gallery.totalAssetsCount', { count: metrics.totalCount })}</span>
            </div>

            <div className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-surface-border bg-surface-elevated text-xs font-mono">
              <Layers className="w-4 h-4 text-emerald-500" />
              <span>{t('gallery.totalStorageUsed', { size: metrics.formattedSize })}</span>
            </div>

            <div className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-surface-border bg-surface-elevated text-xs font-mono">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <span>{t('gallery.totalCreditsUsed', { credits: metrics.totalCredits })}</span>
            </div>

            <button
              type="button"
              onClick={toggleSelectionMode}
              className={`px-3.5 py-2 rounded-xl border text-xs font-semibold flex items-center gap-1.5 transition-colors ${
                isSelectionMode
                  ? 'bg-primary border-primary text-primary-fg shadow-glow-primary/20'
                  : 'border-surface-border bg-surface-elevated hover:bg-surface-border text-foreground'
              }`}
            >
              <CheckSquare className="w-3.5 h-3.5" />
              <span>{isSelectionMode ? t('gallery.cancelSelect') : t('gallery.batchSelect')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* 2. Search & Multi-Dimensional Filter Toolbar */}
      <div className="p-4 rounded-2xl border border-surface-border bg-surface shadow-sm space-y-4">
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
          {/* Search Input */}
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-foreground-muted absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('gallery.searchPlaceholder')}
              className="w-full pl-10 pr-9 py-2 rounded-xl border border-surface-border bg-surface-elevated text-xs text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-muted hover:text-foreground"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Type Filters Tabs */}
          <div className="flex items-center gap-1 p-1 rounded-xl bg-surface-elevated border border-surface-border overflow-x-auto flex-shrink-0">
            {(
              [
                { id: 'all', labelKey: 'gallery.filterAll', icon: Layers },
                { id: 'image', labelKey: 'gallery.filterImages', icon: ImageIcon },
                { id: 'video', labelKey: 'gallery.filterVideos', icon: Film },
                { id: 'audio', labelKey: 'gallery.filterAudio', icon: Volume2 },
              ] as const
            ).map((tab) => {
              const Icon = tab.icon;
              const isActive = typeFilter === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setTypeFilter(tab.id as 'all' | MediaAssetType)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                    isActive
                      ? 'bg-primary text-primary-fg shadow-sm'
                      : 'text-foreground-muted hover:text-foreground hover:bg-surface'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{t(tab.labelKey)}</span>
                </button>
              );
            })}
          </div>

          {/* Model Filter, Sort & Favorites */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Favorites Toggle */}
            <button
              type="button"
              onClick={() => setOnlyFavorites(!onlyFavorites)}
              className={`px-3 py-1.5 rounded-xl border text-xs font-semibold flex items-center gap-1.5 transition-colors ${
                onlyFavorites
                  ? 'border-rose-500/40 bg-rose-500/10 text-rose-500 shadow-sm'
                  : 'border-surface-border bg-surface-elevated text-foreground-muted hover:text-foreground'
              }`}
            >
              <Heart className={`w-3.5 h-3.5 ${onlyFavorites ? 'fill-current' : ''}`} />
              <span>{t('gallery.filterFavorites')}</span>
            </button>

            {/* Model Filter Dropdown */}
            <select
              value={modelFilter}
              onChange={(e) => setModelFilter(e.target.value)}
              className="px-3 py-1.5 rounded-xl border border-surface-border bg-surface-elevated text-xs font-medium text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="all">{t('gallery.modelAll')}</option>
              <option value="flux">Flux 1.1 Pro</option>
              <option value="dalle">OpenAI DALL-E 3</option>
              <option value="kling">Kling AI 1.5</option>
              <option value="luma">Luma Dream Machine</option>
              <option value="runway">Runway Gen-3</option>
              <option value="synthesia">Synthesia Studio</option>
              <option value="elevenlabs">ElevenLabs</option>
              <option value="azure">Azure Neural</option>
              <option value="sd-3.5">Stable Diffusion 3.5</option>
            </select>

            {/* Sort Dropdown */}
            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value as MediaSortOption)}
              className="px-3 py-1.5 rounded-xl border border-surface-border bg-surface-elevated text-xs font-medium text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="newest">{t('gallery.sortNewest')}</option>
              <option value="oldest">{t('gallery.sortOldest')}</option>
              <option value="size-desc">{t('gallery.sortSize')}</option>
              <option value="credits-desc">{t('gallery.sortCredits')}</option>
            </select>
          </div>
        </div>

        {/* Floating Batch Selection Bar */}
        {isSelectionMode && (
          <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl bg-primary/10 border border-primary/20 animate-in fade-in duration-200">
            <div className="flex items-center gap-3 text-xs">
              <span className="font-bold text-primary">
                {t('gallery.selectedCount', { count: selectedIds.length })}
              </span>

              <button
                type="button"
                onClick={selectAll}
                className="text-foreground-muted hover:text-foreground font-medium underline transition-colors"
              >
                {t('gallery.selectAll')}
              </button>

              <button
                type="button"
                onClick={deselectAll}
                className="text-foreground-muted hover:text-foreground font-medium underline transition-colors"
              >
                {t('gallery.deselectAll')}
              </button>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={selectedIds.length === 0}
                onClick={() => setShowBatchDeleteModal(true)}
                className="px-3 py-1.5 rounded-lg bg-rose-600 text-white text-xs font-bold shadow-sm hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>{t('gallery.deleteSelected', { count: selectedIds.length })}</span>
              </button>

              <button
                type="button"
                onClick={toggleSelectionMode}
                className="px-3 py-1.5 rounded-lg border border-surface-border bg-surface text-foreground-muted text-xs hover:bg-surface-elevated"
              >
                {t('gallery.cancelSelect')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 3. Media Grid Stage */}
      {filteredAssets.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filteredAssets.map((asset) => (
            <MediaGridCard
              key={asset.id}
              asset={asset}
              isSelected={selectedIds.includes(asset.id)}
              isSelectionMode={isSelectionMode}
              onSelect={toggleSelect}
              onToggleFavorite={toggleFavorite}
              onInspect={setActiveAsset}
            />
          ))}
        </div>
      ) : (
        /* Empty State */
        <div className="p-12 text-center rounded-2xl border border-dashed border-surface-border bg-surface space-y-4">
          <div className="w-14 h-14 rounded-2xl bg-surface-elevated text-foreground-muted mx-auto flex items-center justify-center">
            <Layers className="w-7 h-7" />
          </div>

          <div className="max-w-md mx-auto space-y-1">
            <h3 className="font-heading font-bold text-base text-foreground">
              {searchQuery || modelFilter !== 'all' || onlyFavorites
                ? t('gallery.noAssetsFound')
                : t('gallery.noAssetsYet')}
            </h3>
            <p className="text-xs text-foreground-muted">
              {searchQuery || modelFilter !== 'all' || onlyFavorites
                ? t('gallery.noAssetsFoundDesc')
                : t('gallery.noAssetsYetDesc')}
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
            <button
              type="button"
              onClick={() => onNavigateToImage?.()}
              className="px-4 py-2 rounded-xl bg-primary text-primary-fg text-xs font-semibold shadow-glow-primary/20 flex items-center gap-1.5 hover:opacity-95 transition-all"
            >
              <ImageIcon className="w-4 h-4" />
              <span>{t('gallery.goToImageStudio')}</span>
            </button>

            <button
              type="button"
              onClick={() => onNavigateToVideo?.()}
              className="px-4 py-2 rounded-xl border border-surface-border bg-surface-elevated text-foreground text-xs font-semibold hover:bg-surface-border flex items-center gap-1.5 transition-colors"
            >
              <Film className="w-4 h-4 text-emerald-500" />
              <span>{t('gallery.goToVideoStudio')}</span>
            </button>

            <button
              type="button"
              onClick={() => onNavigateToAvatar?.()}
              className="px-4 py-2 rounded-xl border border-surface-border bg-surface-elevated text-foreground text-xs font-semibold hover:bg-surface-border flex items-center gap-1.5 transition-colors"
            >
              <Volume2 className="w-4 h-4 text-amber-500" />
              <span>{t('gallery.goToAvatarStudio')}</span>
            </button>
          </div>
        </div>
      )}

      {/* 4. Batch Delete Confirmation Modal */}
      {showBatchDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="w-full max-w-md rounded-2xl border border-surface-border bg-surface p-6 shadow-2xl space-y-4">
            <div className="w-12 h-12 rounded-xl bg-rose-500/10 text-rose-500 flex items-center justify-center">
              <Trash2 className="w-6 h-6" />
            </div>

            <div className="space-y-1">
              <h3 className="font-heading font-bold text-base text-foreground">
                {t('gallery.deleteSelected', { count: selectedIds.length })}
              </h3>
              <p className="text-xs text-foreground-muted leading-relaxed">
                {t('gallery.deleteSelectedConfirm', { count: selectedIds.length })}
              </p>
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => setShowBatchDeleteModal(false)}
                className="px-4 py-2 rounded-xl border border-surface-border bg-surface text-foreground-muted hover:bg-surface-elevated text-xs font-semibold transition-colors"
              >
                {t('gallery.cancelSelect')}
              </button>

              <button
                type="button"
                onClick={handleBatchDelete}
                className="px-4 py-2 rounded-xl bg-rose-600 text-white text-xs font-bold hover:bg-rose-700 shadow-sm transition-colors"
              >
                {t('gallery.deleteSelected', { count: selectedIds.length })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 5. Asset Detail Inspector Drawer */}
      <AssetDetailDrawer
        asset={activeAsset}
        onClose={() => setActiveAsset(null)}
        onToggleFavorite={toggleFavorite}
        onRename={handleRename}
        onDelete={handleSingleDelete}
        onReusePrompt={handleReusePrompt}
      />
    </div>
  );
};
